import {
    createDatabaseConnection,
    updateDatabaseConnection,
    getDatabaseConnectionsByUser,
    softDeleteDatabaseConnection,
    getDatabaseConnection,
    createFileConnectionRecord,
    getFileUploadUrl as apiGetFileUploadUrl,
    confirmFileUpload as apiConfirmFileUpload,
    getSourceConnectionById as apiGetSourceConnectionById,
    CONNECTION_TYPES
} from '../api/SourceDatabase';
import { AWS_REGION, DB_CONNECTIONS_TABLE_NAME } from '../env';

/**
 * Service to handle data source operations
 */
class DataSourceService {

    // Map API connection types to UI types
    static mapApiTypeToUiType(apiType) {
        switch (apiType) {
            case CONNECTION_TYPES.MYSQL:
            case CONNECTION_TYPES.POSTGRESQL:
            case CONNECTION_TYPES.SQL_SERVER:
            case CONNECTION_TYPES.ORACLE:
            case CONNECTION_TYPES.AURORA:
            case CONNECTION_TYPES.SNOWFLAKE:
            case CONNECTION_TYPES.REDSHIFT:
            case CONNECTION_TYPES.BIGQUERY:
            case CONNECTION_TYPES.SYNAPSE:
                return 'SQL';
            case 'excel':
            case 'csv':
                return 'Excel';
            // Add more mappings as needed
            default:
                return apiType.toUpperCase();
        }
    }

    // Map UI types to API connection types
    static mapUiTypeToApiType(uiType) {
        switch (uiType) {
            case 'database':
                return CONNECTION_TYPES.POSTGRESQL; // Default DB type
            case 'erp':
                return 'erp';
            case 'crm':
                return 'crm';
            case 'file':
                return 'excel';
            default:
                return CONNECTION_TYPES.POSTGRESQL;
        }
    }

    // Transform API data source to UI data source
    static transformApiToUiDataSource(apiDataSource) {
        console.log('Transformando datos de la API a UI:', apiDataSource);
        // Verificar si la fuente de datos ha sido probada
        // Si tiene last_successful_connection, sabemos que se probó con éxito
        // Si no tiene ese campo pero tiene last_connection_attempt, sabemos que se intentó probar
        const hasBeenTested = apiDataSource.last_successful_connection || apiDataSource.last_connection_attempt;

        // Si hay un connection_status explícito, usarlo; si no, determinarlo en base a otros campos
        const connectionStatus = apiDataSource.connection_status || 'pending';

        // Determinar la fecha de última sincronización
        let lastSyncDate;
        if (apiDataSource.last_successful_connection) {
            // Si hay una conexión exitosa, usar esa fecha
            lastSyncDate = new Date(apiDataSource.last_successful_connection).toLocaleString();
            console.log('Usando fecha de conexión exitosa:', lastSyncDate);
        } else if (hasBeenTested) {
            // Si se ha intentado probar pero no fue exitosa, usar la fecha del intento
            lastSyncDate = new Date(apiDataSource.last_connection_attempt).toLocaleString();
            console.log('Usando fecha de intento de conexión:', lastSyncDate);
        } else if (apiDataSource.ingestion_date) {
            // Conexiones de archivo (Excel/CSV): nunca pasan por "probar
            // conexión" (last_successful_connection/last_connection_attempt
            // quedan siempre vacíos, esos campos son del flujo de conexión
            // relacional en vivo) -- file-ingestion sí graba ingestion_date
            // cuando termina de procesar el archivo, así que es la señal
            // real de "última sincronización" para este tipo de conexión.
            lastSyncDate = new Date(apiDataSource.ingestion_date).toLocaleString();
            console.log('Usando fecha de ingesta (conexión de archivo):', lastSyncDate);
        } else {
            // Si nunca se ha probado la conexión, mostrar "Nunca"
            lastSyncDate = "Nunca";
            console.log('La fuente nunca ha sido probada');
        }

        const connectionId = apiDataSource.source_connection_id || apiDataSource.connection_id || apiDataSource.id;

        return {
            id: connectionId,
            name: apiDataSource.enterprise_application_name,
            type: this.mapApiTypeToUiType(apiDataSource.connection_type),
            description: apiDataSource.description,
            // REMOVED: url field no longer uses secret_manager_name
            // url: apiDataSource.secret_manager_name,
            databaseName: apiDataSource.database_name,
            databaseVersion: apiDataSource.database_version || '',
            isPublic: apiDataSource.is_public === true,
            useSsl: apiDataSource.use_ssl !== false,
            lastSync: lastSyncDate,
            status: connectionStatus, // Usar el estado de la conexión si existe, o 'pending' por defecto
            // User-facing summary of the last error (if any), plus the full
            // technical detail for the "view technical error" icon.
            errorMessageUser: apiDataSource.error_message_user || '',
            errorMessage: apiDataSource.error_message || '',
            account_id: apiDataSource.account_id || apiDataSource.accountId || null,  // Company/account ID
            created_by: apiDataSource.created_by || null,  // Creator user ID
            // Owner information (will be populated when backend provides it)
            owner_name: apiDataSource.owner_name || null,
            owner_email: apiDataSource.owner_email || null,
            // File connection: extract original file name from s3 key (Requirement 7.9)
            fileName: apiDataSource.original_s3_key
                ? apiDataSource.original_s3_key.split('/').pop()
                : (apiDataSource.file_name || null),
            // Raw connection_type (e.g. 'excel'/'csv') — needed to reopen a file
            // connection for editing with the correct sub-type pre-selected.
            connectionType: apiDataSource.connection_type || '',
            // Snowflake: método de autenticación y parámetros de sesión (no
            // sensibles; la clave privada nunca se devuelve).
            authMethod: apiDataSource.auth_method || 'password',
            warehouse: apiDataSource.warehouse || '',
            role: apiDataSource.role || '',
            schema: apiDataSource.schema || '',
            keyFingerprint: apiDataSource.key_fingerprint || '',
            // Modo de la última carga de archivo ('replace' | 'append').
            loadMode: apiDataSource.load_mode || 'replace',
            // Ingesta multi-hoja: contadores livianos del listado (sin el
            // detalle completo por hoja, ver getRawSourceConnection) para
            // que la tarjeta muestre "X/Y hojas" incluso sin polling activo
            // en memoria (p. ej. justo después de recargar la página).
            totalSheets: apiDataSource.total_sheets ?? null,
            sheetsCompleted: apiDataSource.sheets_completed ?? null,
            sheetsFailed: apiDataSource.sheets_failed ?? null
        };
    }

    // Transform UI form data to API data source
    static transformUiToApiDataSource(uiFormData) {
        console.log('## transformUiToApiDataSource - datos de entrada: ', uiFormData);

        // Validate that we have required fields for the new API
        if (!uiFormData.host && uiFormData.type === 'database') {
            console.warn('¡Advertencia! No se proporcionó un host de base de datos');
        }
        if (!uiFormData.username && uiFormData.type === 'database') {
            console.warn('¡Advertencia! No se proporcionó un usuario de base de datos');
        }
        if (!uiFormData.password && uiFormData.type === 'database') {
            console.warn('¡Advertencia! No se proporcionó una contraseña de base de datos');
        }

        // Generate a UUID for the connection if not exists
        const generateUuid = () => {
            // Simple UUID v4 generator
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
            });
        };

        console.log('## source_connection_id: ', uiFormData.id || generateUuid());

        // Determine the actual connection type
        // If databaseType is specified, use it directly (mysql, postgresql, oracle, etc.)
        // Otherwise, fall back to mapping the generic type
        let connectionType;
        if (uiFormData.databaseType) {
            connectionType = uiFormData.databaseType.toLowerCase();
        } else {
            connectionType = this.mapUiTypeToApiType(uiFormData.type);
        }

        console.log('## connection_type determined:', connectionType, 'from databaseType:', uiFormData.databaseType, 'or type:', uiFormData.type);

        const apiData = {
            source_connection_id: uiFormData.id || generateUuid(),
            
            // NEW API FORMAT: Pass credentials directly for encryption
            // Combine host and port in format "host:port" for backend compatibility
            host: uiFormData.port ? `${uiFormData.host}:${uiFormData.port}` : (uiFormData.host || ''),
            username: uiFormData.username || '', // Database username
            password: uiFormData.password || '', // Database password
            
            connection_type: connectionType,
            database_name: uiFormData.database || uiFormData.databaseName || '', // Asegurar que siempre tengamos un valor, aunque sea vacío
            database_version: uiFormData.databaseVersion || '',
            enterprise_application_name: uiFormData.name,
            description: uiFormData.description || '',
            is_public: uiFormData.isPublic === true,
            use_ssl: uiFormData.useSsl !== false, // Por defecto true si no se especifica
            connection_status: uiFormData.connectionStatus || undefined, // Pasar el estado de conexión si existe
            account_id: uiFormData.account_id || uiFormData.accountId || undefined
        };

        // Snowflake: identificador de cuenta (sin puerto), par de claves en
        // vez de contraseña, y warehouse/rol/schema. La clave generada viaja
        // solo como token cifrado; la subida, como PEM (el backend la cifra).
        if (connectionType === 'snowflake') {
            apiData.host = uiFormData.host || '';
            delete apiData.password;
            // Edición: vacío = sin cambio (la cuenta/usuario guardados están cifrados).
            if (!apiData.host) delete apiData.host;
            if (!apiData.username) delete apiData.username;
            apiData.auth_method = 'key_pair';
            apiData.warehouse = uiFormData.warehouse || '';
            apiData.role = uiFormData.role || '';
            apiData.schema = uiFormData.schema || '';
            if (uiFormData.snowflakeKeyToken) {
                apiData.generated_key_token = uiFormData.snowflakeKeyToken;
            } else if (uiFormData.privateKeyPem) {
                apiData.private_key_pem = uiFormData.privateKeyPem;
                if (uiFormData.privateKeyPassphrase) apiData.private_key_passphrase = uiFormData.privateKeyPassphrase;
            }
        }

        console.log('transformUiToApiDataSource - datos transformados: ', apiData);
        return apiData;
    }

    /**
     * Fetch all data sources for the current user
     */
    static async fetchDataSources() {
        try {
            // Código para obtener datos reales
            console.log('Buscando fuentes de datos en tabla:', DB_CONNECTIONS_TABLE_NAME, 'en región:', AWS_REGION);

            try {
                const result = await getDatabaseConnectionsByUser();
                if (!result.success) {
                    throw new Error(result.error || 'Error fetching data sources');
                }
                return result.data.map(dataSource => this.transformApiToUiDataSource(dataSource));
            } catch (dynamoError) {
                console.error('Error al consultar DynamoDB:', dynamoError);

                // Si hay un error de índice, mostramos un mensaje más claro
                if (dynamoError.message && dynamoError.message.includes('index')) {
                    throw new Error('La tabla no tiene el índice especificado. Por favor configure la tabla DynamoDB correctamente.');
                }

                throw dynamoError;
            }
        } catch (error) {
            console.error('Failed to fetch data sources:', error);

            // Si la tabla no existe y estamos en modo desarrollo, devolvemos un array vacío
            // para que la interfaz muestre el estado vacío en lugar de un error
            if (process.env.NODE_ENV === 'development' &&
                error.message && error.message.includes('ResourceNotFoundException')) {
                console.log('La tabla no existe, en un entorno de producción deberías crearla primero');
                return [];
            }

            throw error;
        }
    }


    /**
     * Create a new data source
     */
    static async createDataSource(formData) {
        try {
            const apiDataSource = this.transformUiToApiDataSource(formData);

            console.log(' ### Creando nueva fuente de datos con:', apiDataSource);

            // Código para crear datos reales
            const result = await createDatabaseConnection(apiDataSource);
            if (!result.success) {
                throw new Error(result.error || 'Error creating data source');
            }
            return this.transformApiToUiDataSource(result.data);

        } catch (error) {
            console.error('Failed to create data source:', error);
            throw error;
        }
    }


    /**
     * Update an existing data source
     */
    static async updateDataSource(id, formData) {
        try {
            const apiDataSource = this.transformUiToApiDataSource(formData);

            // Código para actualizar datos reales
            const result = await updateDatabaseConnection(id, apiDataSource);
            if (!result.success) {
                throw new Error(result.error || 'Error updating data source');
            }
            return this.transformApiToUiDataSource(result.data);
        } catch (error) {
            console.error('Failed to update data source:', error);
            throw error;
        }
    }

    /**
     * Update only name/description/is_public on a file-type (excel/csv)
     * connection. Deliberately bypasses transformUiToApiDataSource/updateDataSource:
     * that path always includes connection_type/host/username/password/database_name
     * in the payload, which (a) trips the backend's relational-only connection_type
     * allowlist and (b) would clobber database_name (holds the Glue database name
     * set by file-ingestion) with an empty string. Sending only these three fields
     * relies on the backend's sparse/partial update (untouched fields are left as-is).
     */
    static async updateFileConnectionMetadata(id, { name, description, isPublic, loadMode, ingestionBatchSize }) {
        const result = await updateDatabaseConnection(id, {
            enterprise_application_name: name,
            description: description || '',
            is_public: isPublic === true,
            // Solo al volver a subir un archivo: 'replace' | 'append' (carga incremental).
            ...(loadMode ? { load_mode: loadMode } : {}),
            // Solo al volver a subir un archivo: hojas por bloque (1 o 4).
            ...(ingestionBatchSize ? { ingestion_batch_size: ingestionBatchSize } : {}),
        });
        if (!result.success) {
            throw new Error(result.error || 'Error updating connection metadata');
        }
        return this.transformApiToUiDataSource(result.data);
    }

    /**
     * Delete a data source
     */
    static async deleteDataSource(id) {
        try {
            // Código para eliminar datos reales
            const result = await softDeleteDatabaseConnection(id);
            if (!result.success) {
                throw new Error(result.error || 'Error deleting data source');
            }

            return true;
        } catch (error) {
            console.error('Failed to delete data source:', error);
            throw error;
        }
    }

    /**
     * Test connection to a data source
     * Uses the new dedicated test endpoint that handles encryption/decryption automatically
     */
    static async testConnection(id) {
        try {
            console.log(`Testing connection for ID: ${id}`);
            
            // Use the new test endpoint that handles everything automatically
            const { testDatabaseConnection } = await import('../api/SourceDatabase');
            
            // Call the new test endpoint
            const result = await testDatabaseConnection(id);
            
            if (!result.success) {
                throw new Error(result.error || 'Error testing connection');
            }
            
            // The API response includes test results and updated connection status
            const testResult = result.data.test_result;
            
            return {
                success: testResult.success,
                message: testResult.message || (testResult.success ? 
                    `Connection successful` : 
                    `Connection failed`)
            };
            
        } catch (error) {
            console.error('Error testing connection:', error);
            
            return {
                success: false,
                message: error.message || 'Error testing connection'
            };
        }
    }

    /**
     * Snowflake: genera un par de claves (ver generateSnowflakeKeyPair).
     */
    static async generateSnowflakeKeyPair(params) {
        const { generateSnowflakeKeyPair } = await import('../api/SourceDatabase');
        return generateSnowflakeKeyPair(params);
    }

    /**
     * Snowflake: prueba una conexión guardada con una clave candidata
     * (rotación) sin guardarla.
     */
    static async testSnowflakeCandidateKey(id, generatedKeyToken) {
        const { testSnowflakeCandidateKey } = await import('../api/SourceDatabase');
        try {
            const response = await testSnowflakeCandidateKey(id, generatedKeyToken);
            return { success: response.success === true, message: response.message };
        } catch (error) {
            return { success: false, message: error.message || 'Error al probar la clave nueva' };
        }
    }

    /**
     * Test connection parameters without saving (for form validation)
     */
    static async testConnectionParameters(connectionParams) {
        try {
            console.log('Testing connection parameters:', { ...connectionParams, password: '***' });
            
            // Import the API function
            const { testDatabaseConnectionParameters } = await import('../api/SourceDatabase');
            
            // Call the test endpoint with parameters
            const result = await testDatabaseConnectionParameters(connectionParams);
            
            if (!result.success) {
                throw new Error(result.error || 'Error testing connection');
            }
            
            // The API response includes test results
            const testResult = result.data.test_result;
            
            return {
                success: testResult.success,
                message: testResult.message || (testResult.success ? 
                    'Conexión exitosa' : 
                    'Error en la conexión'),
                details: testResult.error_details
            };
            
        } catch (error) {
            console.error('Error testing connection parameters:', error);
            
            return {
                success: false,
                message: error.message || 'Error al probar la conexión'
            };
        }
    }

    /**
     * Get a data source by ID
     */
    static async getDataSourceById(id) {
        try {
            // Código para obtener datos reales
            const result = await getDatabaseConnection(id);
            if (!result.success) {
                throw new Error(result.error || 'Error getting data source');
            }
            console.log('Datos obtenidos de la fuente:', result.data);
            return this.transformApiToUiDataSource(result.data);
        } catch (error) {
            console.error('Failed to get data source:', error);
            throw error;
        }
    }
    /**
     * Create a file-based source connection record (csv or excel).
     * Bypasses the DB-specific field validation in createDatabaseConnection.
     */
    static async createFileConnection(formData) {
        try {
            const requestBody = {
                enterprise_application_name: formData.name,
                connection_type: formData.connectionType,
                description: formData.description || '',
                is_public: formData.isPublic === true,
            };

            if (formData.connectionType === 'csv' && formData.csvDelimiter !== undefined) {
                requestBody.csv_delimiter = formData.csvDelimiter;
            }

            // Wizard de carga con schema/nombre de tabla explícitos (ver
            // FileUploadWizard) -- opcionales, sin ellos el comportamiento
            // es el de siempre (schema "Default" auto-provisionado, nombre
            // derivado del id de la conexión).
            if (formData.icebergSchemaId) {
                requestBody.iceberg_schema_id = formData.icebergSchemaId;
            }
            if (formData.tableNameOverride) {
                requestBody.table_name_override = formData.tableNameOverride;
            }
            if (formData.sheetTableNameOverrides) {
                requestBody.sheet_table_name_overrides = formData.sheetTableNameOverrides;
            }
            // Hojas que se procesan a la vez (1 o 4), ver ingestionBatchOptions.js.
            if (formData.ingestionBatchSize) {
                requestBody.ingestion_batch_size = formData.ingestionBatchSize;
            }

            // Fase 3 (motor "Estándar", whisperbi-back-standar-ingestion) --
            // ausente en el backend = Spark (comportamiento histórico), así
            // que "standard" se manda siempre que la UI lo muestre elegido:
            // el selector cae a 'standard' cuando uploadEngine no está
            // seteado, y acá tiene que caer al mismo default -- si no, el
            // usuario ve "Estándar" y la carga corre en Spark.
            if ((formData.uploadEngine || 'standard') === 'standard') {
                requestBody.upload_engine = 'standard';
                requestBody.lambda_memory_mb = formData.lambdaMemoryMb || 1024;
            }

            console.log('### createFileConnection:', requestBody);

            const result = await createFileConnectionRecord(requestBody);
            if (!result.success) {
                throw new Error(result.error || 'Error creating file connection');
            }
            return result.data;
        } catch (error) {
            console.error('Failed to create file connection:', error);
            throw error;
        }
    }

    /**
     * Get a presigned S3 URL for uploading a file.
     */
    static async getFileUploadUrl(sourceConnectionId, fileName, contentType) {
        try {
            const result = await apiGetFileUploadUrl(sourceConnectionId, fileName, contentType);
            if (!result.success) {
                throw new Error(result.error || 'Error getting file upload URL');
            }
            return result.data;
        } catch (error) {
            console.error('Failed to get file upload URL:', error);
            throw error;
        }
    }

    /**
     * Confirm that the file has been uploaded to S3.
     */
    static async confirmFileUpload(sourceConnectionId, s3Key) {
        try {
            const result = await apiConfirmFileUpload(sourceConnectionId, s3Key);
            if (!result.success) {
                throw new Error(result.error || 'Error confirming file upload');
            }
            return result.data;
        } catch (error) {
            console.error('Failed to confirm file upload:', error);
            throw error;
        }
    }

    /**
     * Poll the connection status for a given connection ID.
     * Returns the raw connection_status string.
     */
    static async pollConnectionStatus(connectionId) {
        try {
            const result = await apiGetSourceConnectionById(connectionId);
            if (!result.success) {
                throw new Error(result.error || 'Error polling connection status');
            }
            return result.data.connection_status;
        } catch (error) {
            console.error('Failed to poll connection status:', error);
            throw error;
        }
    }

    /**
     * Fetch the raw (untransformed) source connection record — needed to
     * read fields like semantic_database_payload/semantic_table_payload
     * that transformApiToUiDataSource does not carry through.
     */
    static async getRawSourceConnection(connectionId) {
        const result = await apiGetSourceConnectionById(connectionId);
        if (!result.success) {
            throw new Error(result.error || 'Error getting source connection');
        }
        return result.data;
    }
}

export default DataSourceService;
